import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getApiBaseUrl } from '../services/api';
import { useTranslation } from '../context/LanguageContext';
import './DailyReportModal.css';

function DailyReportModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const reportRef = useRef(null);

  // 모달이 열릴 때 일일보고서 데이터 로드
  useEffect(() => {
    if (isOpen) {
      loadReportData();
    }
  }, [isOpen]);

  // 일일보고서 데이터 로드
  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Tenant-ID': 'seoro',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/reports/daily/enterprise`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(t('components.dailyReport.fetchError'));
      }

      const result = await response.json();
      // ApiResponse 형식: { success, data, ... }
      const data = result.data || result;
      setReportData(data);

    } catch (err) {
      console.error('Failed to load daily report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // PDF 다운로드
  const handleDownloadPDF = async () => {
    if (!reportData || !reportRef.current) return;

    try {
      setIsPdfGenerating(true);

      // 보고서 영역의 HTML을 캡처
      const reportElement = reportRef.current;

      // 원래 스타일 저장
      const originalOverflow = reportElement.style.overflow;
      const originalMaxHeight = reportElement.style.maxHeight;
      const scrollPosition = reportElement.scrollTop;

      // PDF 생성을 위해 임시로 스크롤 제거 및 전체 높이 표시
      reportElement.style.overflow = 'visible';
      reportElement.style.maxHeight = 'none';
      reportElement.scrollTop = 0;

      // PDF 생성을 위해 임시로 닫기 버튼 숨기기
      const closeBtn = reportElement.querySelector('.close-btn');
      const pdfBtn = reportElement.querySelector('.pdf-btn');
      if (closeBtn) closeBtn.style.display = 'none';
      if (pdfBtn) pdfBtn.style.display = 'none';

      // 약간의 지연을 주어 DOM이 완전히 렌더링되도록 함
      await new Promise(resolve => setTimeout(resolve, 100));

      // HTML을 캔버스로 변환 (전체 높이 캡처)
      const canvas = await html2canvas(reportElement, {
        scale: 2, // 고해상도
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowHeight: reportElement.scrollHeight,
        height: reportElement.scrollHeight
      });

      // 원래 스타일 복원
      reportElement.style.overflow = originalOverflow;
      reportElement.style.maxHeight = originalMaxHeight;
      if (closeBtn) closeBtn.style.display = 'flex';
      if (pdfBtn) pdfBtn.style.display = 'flex';
      reportElement.scrollTop = scrollPosition;

      // 캔버스를 이미지로 변환
      const imgData = canvas.toDataURL('image/png');

      // PDF 생성 (A4 사이즈)
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // 픽셀을 mm로 변환 (96 DPI 기준)
      const imgWidthMM = (imgWidth * 25.4) / 96 / 2; // scale 2 적용
      const imgHeightMM = (imgHeight * 25.4) / 96 / 2; // scale 2 적용

      // PDF 너비에 맞게 비율 조정
      const ratio = pdfWidth / imgWidthMM;
      const scaledImgHeight = imgHeightMM * ratio;

      let heightLeft = scaledImgHeight;
      let position = 0;
      let pageCount = 0;

      // 첫 페이지
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledImgHeight);
      heightLeft -= pdfHeight;
      pageCount++;

      // 나머지 페이지들 - 각 페이지마다 다음 부분을 표시
      while (heightLeft > 0) {
        position = -(pdfHeight * pageCount);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledImgHeight);
        heightLeft -= pdfHeight;
        pageCount++;
      }

      // PDF 파일명 생성
      const dateStr = new Date(reportData.header.reportDate).toLocaleDateString('ko-KR').replace(/\. /g, '-').replace('.', '');
      const fileName = t('components.dailyReport.fileName', { category: reportData.header.category, date: dateStr });

      // PDF 다운로드
      pdf.save(fileName);

    } catch (error) {
      console.error('PDF 생성 실패:', error);
      alert(t('components.dailyReport.pdfFailed'));
    } finally {
      setIsPdfGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="daily-report-modal-overlay" onClick={onClose}>
      <div className="daily-report-modal-content" onClick={(e) => e.stopPropagation()} ref={reportRef}>
        {/* 로딩 중 */}
        {loading && (
          <div className="report-loading">
            <div className="loading-spinner"></div>
            <p>{t('components.dailyReport.loading')}</p>
          </div>
        )}

        {/* 에러 */}
        {error && !loading && (
          <div className="report-error">
            <p>{error}</p>
            <div>
              <button onClick={loadReportData}>{t('components.dailyReport.retry')}</button>
              <button onClick={onClose}>{t('components.dailyReport.close')}</button>
            </div>
          </div>
        )}

        {/* 보고서 내용 */}
        {!loading && !error && reportData && (
          <>
            {/* 모달 헤더 */}
            <div className="report-header">
              <button className="close-btn" onClick={onClose}>✕</button>

              <div className="report-title-section">
                <h1 className="report-main-title">{t('components.dailyReport.title')}</h1>
                <div className="report-subtitle">{t('components.dailyReport.subtitle')}</div>
              </div>

              <div className="report-info-table">
                <table>
                  <tbody>
                    <tr>
                      <th>{t('components.dailyReport.reportDate')}</th>
                      <td>{new Date(reportData.header.reportDate).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                      })}</td>
                      <th>{t('components.dailyReport.category')}</th>
                      <td>{reportData.header.category}</td>
                    </tr>
                    <tr>
                      <th>{t('components.dailyReport.author')}</th>
                      <td>{reportData.header.author}</td>
                      <th>{t('components.dailyReport.createdAt')}</th>
                      <td>{new Date(reportData.header.createdAt).toLocaleString('ko-KR')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모달 바디 */}
            <div className="report-body">
              {/* 1. 설비 현황 */}
              <div className="report-section">
                <h2 className="section-number">{t('components.dailyReport.section1')}</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('components.dailyReport.colItem')}</th>
                      <th>{t('components.dailyReport.colQtyRatio')}</th>
                      <th>{t('components.dailyReport.colUnit')}</th>
                      <th>{t('components.dailyReport.colNote')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t('components.dailyReport.totalEquipment')}</td>
                      <td className="value-cell">{reportData.equipmentStatus.totalEquipment}</td>
                      <td>{t('components.dailyReport.unitDae')}</td>
                      <td>{t('components.dailyReport.totalEquipmentNote')}</td>
                    </tr>
                    <tr className="summary-row">
                      <td><strong>{t('components.dailyReport.avgOperationRate')}</strong></td>
                      <td className="value-cell"><strong>{reportData.equipmentStatus.averageOperationRate.toFixed(1)}</strong></td>
                      <td><strong>{t('components.dailyReport.unitPercent')}</strong></td>
                      <td>{t('components.dailyReport.avgOperationRateNote')}</td>
                    </tr>
                    <tr>
                      <td>{t('components.dailyReport.totalShots')}</td>
                      <td className="value-cell">{reportData.equipmentStatus.totalShots.toLocaleString()}</td>
                      <td>{t('components.dailyReport.unitTimes')}</td>
                      <td>{t('components.dailyReport.totalShotsNote')}</td>
                    </tr>
                    <tr className="highlight-row danger">
                      <td>{t('components.dailyReport.alarmCount')}</td>
                      <td className="value-cell">{reportData.equipmentStatus.totalAlarmCount.toLocaleString()}</td>
                      <td>{t('components.dailyReport.unitCount')}</td>
                      <td>{t('components.dailyReport.alarmCountNote')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 2. 생산 실적 */}
              <div className="report-section">
                <h2 className="section-number">{t('components.dailyReport.section2')}</h2>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('components.dailyReport.colItem')}</th>
                      <th>{t('components.dailyReport.colQty')}</th>
                      <th>{t('components.dailyReport.colUnit')}</th>
                      <th>{t('components.dailyReport.colNote')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="highlight-row success">
                      <td>{t('components.dailyReport.goodProduction')}</td>
                      <td className="value-cell">{reportData.equipmentStatus.dailyProduction.toLocaleString()}</td>
                      <td>{t('components.dailyReport.unitPiece')}</td>
                      <td>{t('components.dailyReport.goodProductionNote')}</td>
                    </tr>
                    <tr className="highlight-row warning">
                      <td>{t('components.dailyReport.defectCount')}</td>
                      <td className="value-cell">{reportData.equipmentStatus.defectCount.toLocaleString()}</td>
                      <td>{t('components.dailyReport.unitPiece')}</td>
                      <td>{t('components.dailyReport.defectCountNote')}</td>
                    </tr>
                    <tr className="summary-row">
                      <td><strong>{t('components.dailyReport.totalProduction')}</strong></td>
                      <td className="value-cell"><strong>{(reportData.equipmentStatus.dailyProduction + reportData.equipmentStatus.defectCount).toLocaleString()}</strong></td>
                      <td><strong>{t('components.dailyReport.unitPiece')}</strong></td>
                      <td>{t('components.dailyReport.totalProductionNote')}</td>
                    </tr>
                    <tr>
                      <td>{t('components.dailyReport.energyUsage')}</td>
                      <td className="value-cell">{reportData.equipmentStatus.energyProduction.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>kWh</td>
                      <td>{t('components.dailyReport.energyUsageNote')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 3. OEE 지표 */}
              <div className="report-section">
                <h2 className="section-number">{t('components.dailyReport.section3')}</h2>
                <table className="data-table oee-table">
                  <thead>
                    <tr>
                      <th>{t('components.dailyReport.oeeIndicator')}</th>
                      <th>{t('components.dailyReport.oeeValue')}</th>
                      <th>{t('components.dailyReport.oeeProgress')}</th>
                      <th>{t('components.dailyReport.oeeRating')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t('components.dailyReport.availability')}</td>
                      <td className="value-cell">{reportData.oee.availability.toFixed(1)}%</td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill availability" style={{ width: `${reportData.oee.availability}%` }}></div>
                        </div>
                      </td>
                      <td>{reportData.oee.availability >= 85 ? t('components.dailyReport.ratingGood') : reportData.oee.availability >= 70 ? t('components.dailyReport.ratingAverage') : t('components.dailyReport.ratingNeedsImprovement')}</td>
                    </tr>
                    <tr>
                      <td>{t('components.dailyReport.performance')}</td>
                      <td className="value-cell">{reportData.oee.performance.toFixed(1)}%</td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill performance" style={{ width: `${reportData.oee.performance}%` }}></div>
                        </div>
                      </td>
                      <td>{reportData.oee.performance >= 85 ? t('components.dailyReport.ratingGood') : reportData.oee.performance >= 70 ? t('components.dailyReport.ratingAverage') : t('components.dailyReport.ratingNeedsImprovement')}</td>
                    </tr>
                    <tr>
                      <td>{t('components.dailyReport.quality')}</td>
                      <td className="value-cell">{reportData.oee.quality.toFixed(1)}%</td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill quality" style={{ width: `${reportData.oee.quality}%` }}></div>
                        </div>
                      </td>
                      <td>{reportData.oee.quality >= 95 ? t('components.dailyReport.ratingGood') : reportData.oee.quality >= 90 ? t('components.dailyReport.ratingAverage') : t('components.dailyReport.ratingNeedsImprovement')}</td>
                    </tr>
                    <tr className="summary-row oee-total">
                      <td><strong>{t('components.dailyReport.overallOee')}</strong></td>
                      <td className="value-cell"><strong>{reportData.oee.oee.toFixed(1)}%</strong></td>
                      <td className="progress-cell">
                        <div className="progress-bar">
                          <div className="progress-fill oee" style={{ width: `${reportData.oee.oee}%` }}></div>
                        </div>
                      </td>
                      <td><strong>{reportData.oee.oee >= 75 ? t('components.dailyReport.ratingExcellent') : reportData.oee.oee >= 60 ? t('components.dailyReport.ratingGood') : t('components.dailyReport.ratingNeedsImprovement')}</strong></td>
                    </tr>
                  </tbody>
                </table>
                <div className="oee-formula">
                  <p>{t('components.dailyReport.oeeFormula')}</p>
                </div>
              </div>

              {/* 4. AI 종합 분석 */}
              <div className="report-section">
                <h2 className="section-number">{t('components.dailyReport.section4')}</h2>
                <div className="ai-summary-box markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {reportData.aiSummary}
                  </ReactMarkdown>
                </div>
              </div>

              {/* 보고서 하단 */}
              <div className="report-footer">
                <div className="footer-actions">
                  <button className="pdf-btn" onClick={handleDownloadPDF} disabled={isPdfGenerating}>
                    {isPdfGenerating ? (
                      <>
                        <span className="pdf-loading-spinner"></span> {t('components.dailyReport.pdfGenerating')}
                      </>
                    ) : (
                      <>
                        <span>📄</span> {t('components.dailyReport.pdfSave')}
                      </>
                    )}
                  </button>
                </div>
                <div className="footer-info">
                  <p>{t('components.dailyReport.autoFooter')}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DailyReportModal;
